import { computed, ref } from 'vue'
import { useGang } from '~/composables/useGang'
import {
  gangNamesFor, loadChosenWarehouse, loadSiteGangs, saveChosenWarehouse, saveSiteGangs, settleWarehouse, warehousesOf,
  type GangEntry, type Warehouse,
} from '~/utils/warehouses'

/**
 * The warehouse this tablet is working in, and the gangs it can offer
 * (utils/warehouses.ts has the rules and the reasons).
 *
 * Module state, like the gang: one answer for the whole page. It starts from
 * what the tablet kept, so it has an answer before any request lands and when
 * there is no signal; `noteGangs` replaces it whenever the gang list is read.
 */
const gangs = ref<GangEntry[]>(loadSiteGangs())
const chosen = ref<Warehouse | null>(loadChosenWarehouse())
/** Whether the gang list has been read since this page loaded. */
const readThisPage = ref(false)

export function useWarehouse() {
  const { clearGang } = useGang()

  const warehouses = computed(() => warehousesOf(gangs.value))
  const settled = computed(() => settleWarehouse(warehouses.value, chosen.value))
  /** The warehouse in use; null where the site has none, or none is chosen yet. */
  const warehouse = computed(() => settled.value.warehouse)
  /** Several warehouses and nothing valid chosen: ask (pages/warehouse.vue). */
  const mustChoose = computed(() => settled.value.mustChoose)
  /** The gangs to offer here. */
  const gangNames = computed(() => gangNamesFor(gangs.value, warehouse.value?.id ?? null))

  /** The gang list as just read from the server. */
  function noteGangs(latest: GangEntry[]) {
    gangs.value = latest
    readThisPage.value = true
    saveSiteGangs(latest)
  }

  /** Pick the warehouse. A different one drops the gang: gangs belong to a warehouse. */
  function choose(next: Warehouse) {
    const changed = warehouse.value?.id !== next.id
    chosen.value = next
    saveChosenWarehouse(next)
    if (changed) clearGang()
  }

  return { gangs: computed(() => gangs.value), warehouses, warehouse, mustChoose, gangNames, readThisPage, noteGangs, choose }
}
